import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchUnifiedSummary,
  fetchUnifiedPayments,
  fetchSubscriptionPayments,
  updateSubscriptionPayment,
  deleteSubscriptionPayment,
  deleteEventPayment,
  fetchCoursePaymentsAdmin,
  updateCoursePaymentAdmin,
  deleteCoursePaymentAdmin,
} from "../../services/paymentService";
import "./paymentDashboard.css";

const TABS = ["All", "Subscriptions", "Events", "Courses"];

const TYPE_BADGE = {
  SUBSCRIPTION: { cls: "pd-badge-blue",   label: "Subscription" },
  EVENT:        { cls: "pd-badge-purple", label: "Event" },
  COURSE:       { cls: "pd-badge-green",  label: "Course" },
};
const STATUS_BADGE = {
  SUCCESS: { cls: "pd-badge-green",  label: "Paid" },
  FAILED:  { cls: "pd-badge-red",    label: "Failed" },
  PENDING: { cls: "pd-badge-amber",  label: "Pending" },
  CREATED: { cls: "pd-badge-amber",  label: "Pending" },
  ADMIN:   { cls: "pd-badge-indigo", label: "Admin Grant" },
  DELETED: { cls: "pd-badge-gray",   label: "Deleted" },
};
const SUBTITLE_MAP = {
  GROUP: "Group", ONE_ON_ONE: "1-on-1", PARTNER_PRACTICE: "Partner", COMBO: "Combo",
};

const fmtAmt      = (v) => v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";
const fmtAmtPaise = (v) => v != null ? `₹${(Number(v) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";
const fmtDT   = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const emptyF  = () => ({ search: "", type: "", status: "", startDate: "", endDate: "", minAmount: "", maxAmount: "" });

const Badge = ({ type, map }) => {
  const c = map[type] || { cls: "pd-badge-gray", label: type || "—" };
  return <span className={`pd-badge ${c.cls}`}>{c.label}</span>;
};

const UserCell = ({ userId, userName, userEmail, navigate }) => (
  <div>
    {userId != null
      ? <span className="pd-user-link" onClick={() => navigate(`/admin/users/${userId}`)}>{userName || "—"}</span>
      : <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{userName || "—"}</span>}
    {userEmail && <div className="pd-cell-sub">{userEmail}</div>}
  </div>
);

const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return <div className={`pd-toast ${type === "error" ? "pd-toast-error" : "pd-toast-success"}`}>{msg}</div>;
};

const ConfirmDialog = ({ message, warning, onConfirm, onCancel, confirmLabel = "Confirm", danger }) => (
  <div className="pd-overlay">
    <div className="pd-modal pd-modal-sm">
      <p className="pd-confirm-msg">{message}</p>
      {warning && <div className="pd-warning-banner">{warning}</div>}
      <div className="pd-modal-actions">
        <button className={`pd-btn ${danger ? "pd-btn-danger" : "pd-btn-primary"}`} onClick={onConfirm}>{confirmLabel}</button>
        <button className="pd-btn pd-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

const Pagination = ({ page, totalPages, onPage }) => {
  if (totalPages <= 1) return null;
  const all = Array.from({ length: totalPages }, (_, i) => i);
  const vis = all.filter((i) => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1);
  return (
    <div className="pd-pagination">
      <button className="pd-btn pd-btn-ghost pd-btn-sm" disabled={page === 0} onClick={() => onPage(page - 1)}>← Prev</button>
      {vis.map((i, idx) => (
        <span key={i}>
          {idx > 0 && vis[idx - 1] !== i - 1 && <span className="pd-ellipsis">…</span>}
          <button className={`pd-btn pd-btn-sm ${page === i ? "pd-btn-primary" : "pd-btn-ghost"}`} onClick={() => onPage(i)}>{i + 1}</button>
        </span>
      ))}
      <button className="pd-btn pd-btn-ghost pd-btn-sm" disabled={page === totalPages - 1} onClick={() => onPage(page + 1)}>Next →</button>
    </div>
  );
};

const SummaryStrip = ({ s }) => {
  if (!s) return null;
  return (
    <div className="pd-summary-strip">
      {[
        { label: "Total Revenue",      value: fmtAmt(s.totalRevenue),        cls: "pd-stat-primary" },
        { label: "Subscription",       value: fmtAmt(s.subscriptionRevenue), sub: `${s.subscriptionCount} txns` },
        { label: "Events",             value: fmtAmt(s.eventRevenue),        sub: `${s.eventCount} txns` },
        { label: "Courses",            value: fmtAmt(s.courseRevenue),       sub: `${s.courseCount} txns` },
        { label: "✅ Success",         value: s.successCount,                cls: "pd-stat-success" },
        { label: "❌ Failed",          value: s.failedCount,                 cls: "pd-stat-failed" },
        { label: "⏳ Pending",         value: s.pendingCount,                cls: "pd-stat-pending" },
        { label: "Total Transactions", value: s.totalTransactions },
      ].map((c) => (
        <div key={c.label} className={`pd-stat-card ${c.cls || ""}`}>
          <div className="pd-stat-label">{c.label}</div>
          <div className="pd-stat-value">{c.value}</div>
          {c.sub && <div className="pd-stat-sub">{c.sub}</div>}
        </div>
      ))}
    </div>
  );
};

const PageTotalBar = ({ rows, activeTab }) => {
  if (!rows || !rows.length) return null;
  const total = rows.reduce((sum, r) => {
    const amt = activeTab === 3 ? (r.amount || 0) / 100 : (r.amount || 0);
    return sum + amt;
  }, 0);
  return (
    <div className="pd-page-total-bar">
      <span className="pd-page-total-label">📄 Page Total &mdash; {rows.length} records shown</span>
      <span className="pd-page-total-value">{fmtAmt(total)}</span>
    </div>
  );
};

const FilterBar = ({ filters, onChange, onApply, onClear, showType }) => (
  <div className="pd-filter-bar">
    <input className="pd-input" placeholder="🔍 Search name / email" value={filters.search} onChange={(e) => onChange("search", e.target.value)} />
    {showType && (
      <select className="pd-select" value={filters.type} onChange={(e) => onChange("type", e.target.value)}>
        <option value="">All Types</option>
        <option value="SUBSCRIPTION">Subscription</option>
        <option value="EVENT">Event</option>
        <option value="COURSE">Course</option>
      </select>
    )}
    <select className="pd-select" value={filters.status} onChange={(e) => onChange("status", e.target.value)}>
      <option value="">All Status</option>
      <option value="SUCCESS">Paid</option>
      <option value="FAILED">Failed</option>
      <option value="PENDING">Pending</option>
      <option value="CREATED">Created</option>
      <option value="ADMIN">Admin Grant</option>
    </select>
    <input className="pd-input pd-input-date" type="date" value={filters.startDate} onChange={(e) => onChange("startDate", e.target.value)} />
    <input className="pd-input pd-input-date" type="date" value={filters.endDate} onChange={(e) => onChange("endDate", e.target.value)} />
    <input className="pd-input pd-input-sm" type="number" placeholder="Min ₹" value={filters.minAmount} onChange={(e) => onChange("minAmount", e.target.value)} />
    <input className="pd-input pd-input-sm" type="number" placeholder="Max ₹" value={filters.maxAmount} onChange={(e) => onChange("maxAmount", e.target.value)} />
    <button className="pd-btn pd-btn-primary pd-btn-sm" onClick={onApply}>Apply</button>
    <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={onClear}>Clear</button>
  </div>
);

const CopyCell = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="pd-cell-sub">—</span>;
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span className="pd-cell-mono">{value}</span>
      <button onClick={copy} title="Copy payment ID"
        style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
          borderRadius: 4, color: copied ? "#16a34a" : "#1e293b", fontSize: "1.25rem", lineHeight: 1 }}>
        {copied ? "✓" : "⎘"}
      </button>
    </div>
  );
};

// ── Tables ────────────────────────────────────────────────────
const AllTable = ({ rows, navigate }) => (
  <table className="pd-table">
    <thead><tr><th>Date</th><th>Type</th><th>User</th><th>Title</th><th>Sub Title</th><th>Amount</th><th>Status</th><th>Payment ID</th></tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r.id}>
        <td className="pd-cell-sub">{fmtDT(r.date)}</td>
        <td><Badge type={r.type} map={TYPE_BADGE} /></td>
        <td><UserCell userId={r.userId} userName={r.userName} userEmail={r.userEmail} navigate={navigate} /></td>
        <td className="pd-cell-bold">{r.title || "—"}</td>
        <td className="pd-cell-sub">{SUBTITLE_MAP[r.subTitle] ?? r.subTitle ?? "—"}</td>
        <td className="pd-cell-amount">{fmtAmt(r.amount)}</td>
        <td><Badge type={r.status} map={STATUS_BADGE} /></td>
        <td><CopyCell value={r.razorpayPaymentId} /></td>
      </tr>
    ))}</tbody>
  </table>
);

const SubTable = ({ rows, navigate, onEdit, onDelete }) => (
  <table className="pd-table">
    <thead><tr><th>Date</th><th>User</th><th>Plan</th><th>Plan Type</th><th>Amount</th><th>Status</th><th>Payment ID</th><th>Actions</th></tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r.id}>
        <td className="pd-cell-sub">{fmtDT(r.date || r.createdAt)}</td>
        <td><UserCell userId={r.userId} userName={r.userName} userEmail={r.userEmail} navigate={navigate} /></td>
        <td className="pd-cell-bold">{r.planName || r.title || "—"}</td>
        <td className="pd-cell-sub">{SUBTITLE_MAP[r.planType || r.subTitle] ?? r.planType ?? r.subTitle ?? "—"}</td>
        <td className="pd-cell-amount">{fmtAmt(r.amount)}</td>
        <td><Badge type={r.status} map={STATUS_BADGE} /></td>
        <td><CopyCell value={r.razorpayPaymentId} /></td>
        <td><div className="pd-cell-actions">
          <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => onEdit(r)}>✏️</button>
          <button className="pd-btn pd-btn-danger-ghost pd-btn-sm" onClick={() => onDelete(r)}>🗑️</button>
        </div></td>
      </tr>
    ))}</tbody>
  </table>
);

const EventTable = ({ rows, navigate, onDelete }) => (
  <table className="pd-table">
    <thead><tr><th>Date</th><th>User</th><th>Event</th><th>Amount</th><th>Status</th><th>Payment ID</th><th>Actions</th></tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r.id}>
        <td className="pd-cell-sub">{fmtDT(r.date)}</td>
        <td><UserCell userId={r.userId} userName={r.userName} userEmail={r.userEmail} navigate={navigate} /></td>
        <td className="pd-cell-bold">{r.title || "—"}</td>
        <td className="pd-cell-amount">{fmtAmt(r.amount)}</td>
        <td><Badge type={r.status} map={STATUS_BADGE} /></td>
        <td><CopyCell value={r.razorpayPaymentId} /></td>
        <td><button className="pd-btn pd-btn-danger-ghost pd-btn-sm" onClick={() => onDelete(r)}>🗑️</button></td>
      </tr>
    ))}</tbody>
  </table>
);

const CourseTable = ({ rows, navigate, onEdit, onDelete }) => (
  <table className="pd-table">
    <thead><tr><th>Date</th><th>User</th><th>Course</th><th>Batch</th><th>Amount</th><th>Status</th><th>Admin Grant</th><th>Payment ID</th><th>Actions</th></tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r.enrollmentId}>
        <td className="pd-cell-sub">{fmtDate(r.enrolledAt)}</td>
        <td><UserCell userId={r.userId} userName={r.userName} userEmail={r.userEmail} navigate={navigate} /></td>
        <td className="pd-cell-bold">{r.courseName || "—"}</td>
        <td className="pd-cell-sub">{r.batchName || "—"}</td>
        <td className="pd-cell-amount">{fmtAmtPaise(r.amount)}</td>
        <td><Badge type={r.paymentStatus} map={STATUS_BADGE} /></td>
        <td>{r.adminGranted ? <span className="pd-badge pd-badge-indigo">Yes</span> : <span className="pd-cell-sub">—</span>}</td>
        <td><CopyCell value={r.razorpayPaymentId} /></td>
        <td><div className="pd-cell-actions">
          <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => onEdit(r)}>✏️</button>
          <button className="pd-btn pd-btn-danger-ghost pd-btn-sm" onClick={() => onDelete(r)}>🗑️</button>
        </div></td>
      </tr>
    ))}</tbody>
  </table>
);

// ── Edit Modals ───────────────────────────────────────────────
const EditSubModal = ({ row, onClose, onSuccess }) => {
  const [status, setStatus] = useState(row.status || "");
  const [payId,  setPayId]  = useState(row.razorpayPaymentId || "");
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");
  const willActivate = status === "SUCCESS" && row.status !== "SUCCESS";

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const body = {};
      if (status !== row.status) body.status = status;
      if (payId !== (row.razorpayPaymentId || "")) body.razorpayPaymentId = payId;
      await updateSubscriptionPayment(row.id, body);
      onSuccess("Subscription payment updated.");
    } catch (e) { setErr(e?.response?.data?.message || "Failed to update."); }
    finally { setSaving(false); }
  };

  return (
    <div className="pd-overlay">
      <div className="pd-modal">
        <h3 className="pd-modal-title">Edit Subscription #{row.id}</h3>
        <div className="pd-cell-sub" style={{ marginBottom: "1rem" }}>{row.userName} — {row.title}</div>
        {willActivate && <div className="pd-warning-banner">⚠️ This will re-activate the user's subscription plan and add sessions to their account. This cannot be undone.</div>}
        <div className="pd-form-group"><label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {["SUCCESS","FAILED","PENDING","CREATED"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="pd-form-group"><label>Razorpay Payment ID</label>
          <input value={payId} onChange={(e) => setPayId(e.target.value)} placeholder="pay_xxx" />
        </div>
        {err && <div className="pd-error">{err}</div>}
        <div className="pd-modal-actions">
          <button className="pd-btn pd-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          <button className="pd-btn pd-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const EditCourseModal = ({ row, onClose, onSuccess }) => {
  const [status, setStatus] = useState(row.enrollmentStatus || row.paymentStatus || "");
  const [payId,  setPayId]  = useState(row.razorpayPaymentId || "");
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await updateCoursePaymentAdmin(row.enrollmentId, { status, razorpayPaymentId: payId });
      onSuccess("Course payment updated.");
    } catch (e) { setErr(e?.response?.data?.message || "Failed to update."); }
    finally { setSaving(false); }
  };

  return (
    <div className="pd-overlay">
      <div className="pd-modal">
        <h3 className="pd-modal-title">Edit Course Payment #{row.enrollmentId}</h3>
        <div className="pd-cell-sub" style={{ marginBottom: "1rem" }}>{row.userName} — {row.courseName} / {row.batchName}</div>
        <div className="pd-form-group"><label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {["ACTIVE","PENDING_PAYMENT","CANCELLED","COMPLETED"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="pd-form-group"><label>Razorpay Payment ID</label>
          <input value={payId} onChange={(e) => setPayId(e.target.value)} placeholder="pay_xxx" />
        </div>
        {err && <div className="pd-error">{err}</div>}
        <div className="pd-modal-actions">
          <button className="pd-btn pd-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          <button className="pd-btn pd-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────
const PaymentDashboard = () => {
  const navigate = useNavigate();
  const [activeTab,   setActiveTab]   = useState(0);
  const [summary,     setSummary]     = useState(null);
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [toast,       setToast]       = useState(null);
  const [pagination,  setPagination]  = useState({ page: 0, totalPages: 0, totalElements: 0 });
  const [filters,     setFilters]     = useState(emptyF());
  const [applied,     setApplied]     = useState(emptyF());
  const [editSub,     setEditSub]     = useState(null);
  const [editCourse,  setEditCourse]  = useState(null);
  const [confirm,     setConfirm]     = useState(null);

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const reloadSummary = () => fetchUnifiedSummary().then((r) => setSummary(r.data)).catch(() => {});

  const load = useCallback(async (page = 0) => {
    setLoading(true); setError("");
    try {
      const p = { page, size: 20 };
      if (applied.search)    p.search    = applied.search;
      if (applied.status)    p.status    = applied.status;
      if (applied.startDate) p.startDate = new Date(applied.startDate).toISOString();
      if (applied.endDate)   p.endDate   = new Date(applied.endDate + "T23:59:59").toISOString();
      if (applied.minAmount) p.minAmount = applied.minAmount;
      if (applied.maxAmount) p.maxAmount = applied.maxAmount;

      let res;
      if (activeTab === 0) { if (applied.type) p.type = applied.type; res = await fetchUnifiedPayments(p); }
      else if (activeTab === 1) res = await fetchSubscriptionPayments(p);
      else if (activeTab === 2) { p.type = "EVENT"; res = await fetchUnifiedPayments(p); }
      else res = await fetchCoursePaymentsAdmin(p);

      const d = res.data;
      setRows(d.content || []);
      setPagination({ page: d.number, totalPages: d.totalPages, totalElements: d.totalElements });
    } catch { setError("Failed to load payments."); }
    finally  { setLoading(false); }
  }, [activeTab, applied]);

  useEffect(() => { reloadSummary(); }, []); // eslint-disable-line
  useEffect(() => { load(0); }, [activeTab, applied]); // eslint-disable-line

  const switchTab = (i) => { setActiveTab(i); setFilters(emptyF()); setApplied(emptyF()); };
  const handleFilterChange = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const handleApply = () => setApplied({ ...filters });
  const handleClear = () => { setFilters(emptyF()); setApplied(emptyF()); };

  const afterAction = (msg) => {
    setEditSub(null); setEditCourse(null); setConfirm(null);
    showToast(msg); load(pagination.page); reloadSummary();
  };

  const confirmDelete = (message, action) => setConfirm({
    message, danger: true, confirmLabel: "Delete",
    onConfirm: async () => { try { await action(); afterAction("Deleted successfully."); } catch { showToast("Failed to delete.", "error"); setConfirm(null); } },
  });

  return (
    <div className="pd-page">
      {toast   && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />}
      {editSub    && <EditSubModal    row={editSub}    onClose={() => setEditSub(null)}    onSuccess={afterAction} />}
      {editCourse && <EditCourseModal row={editCourse} onClose={() => setEditCourse(null)} onSuccess={afterAction} />}

      <div className="pd-header">
        <h2 className="pd-title">Payment Transactions</h2>
      </div>

      <SummaryStrip s={summary} />

      <div className="pd-tabs">
        {TABS.map((t, i) => (
          <button key={t} className={`pd-tab ${activeTab === i ? "pd-tab-active" : ""}`} onClick={() => switchTab(i)}>{t}</button>
        ))}
      </div>

      <FilterBar filters={filters} onChange={handleFilterChange} onApply={handleApply} onClear={handleClear} showType={activeTab === 0} />

      {error && <div className="pd-error-bar">{error}</div>}

      <div className="pd-table-area">
        {loading ? (
          <div className="pd-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="pd-empty">No records found.</div>
        ) : (
          <>
            <PageTotalBar rows={rows} activeTab={activeTab} />
            <div className="pd-count">{pagination.totalElements} record{pagination.totalElements !== 1 ? "s" : ""}</div>
            <div className="pd-table-wrap">
              {activeTab === 0 && <AllTable    rows={rows} navigate={navigate} />}
              {activeTab === 1 && <SubTable    rows={rows} navigate={navigate} onEdit={setEditSub}    onDelete={(r) => confirmDelete("This will soft-delete the payment record. The user's active sessions will NOT be affected. Continue?", () => deleteSubscriptionPayment(r.id))} />}
              {activeTab === 2 && <EventTable  rows={rows} navigate={navigate} onDelete={(r) => confirmDelete("This will permanently delete this event payment record. Continue?", () => deleteEventPayment(r.id))} />}
              {activeTab === 3 && <CourseTable rows={rows} navigate={navigate} onEdit={setEditCourse} onDelete={(r) => confirmDelete("This will permanently delete this course payment record. Continue?", () => deleteCoursePaymentAdmin(r.enrollmentId))} />}
            </div>
            <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={(p) => load(p)} />
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentDashboard;
